import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Play, Pause, ZoomIn, ZoomOut, Image as ImageIcon, Mic, Music, FolderOpen, Upload, Globe, Film, Volume2 } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { useToast } from "../../context/ToastContext";
import useRoughCut from "./useRoughCut";
import ResourceSelector from "../resource/ResourceSelector";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";

const TRACK_LABEL_W = 110;
const TRACK_H = 64;
const RULER_H = 28;
const MIN_PX = 20;
const MAX_PX = 200;
const DEFAULT_PX = 60;
// 播放头颜色走主题 error 变量（全部用于 CSS style，非 canvas 绘制）
const HEAD_COLOR = "var(--color-error)";

// ─── 获取音频时长 ───
const getAudioDuration = (url) => new Promise((resolve) => {
  const audio = new Audio();
  audio.addEventListener("loadedmetadata", () => resolve(audio.duration));
  audio.addEventListener("error", () => resolve(3));
  audio.src = url;
});

// ─── 音频波形组件 ───
const AudioWaveform = React.memo(({ url, width, height, color }) => {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (!url || !canvasRef.current || width < 4) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, height);

    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    fetch(url)
      .then((r) => r.arrayBuffer())
      .then((buf) => audioCtx.decodeAudioData(buf))
      .then((decoded) => {
        const raw = decoded.getChannelData(0);
        // 每 2px 一根柱子，铺满整个素材块宽度
        const barW = 2;
        const gap = 1;
        const bars = Math.max(1, Math.floor(width / (barW + gap)));
        const step = Math.max(1, Math.floor(raw.length / bars));
        ctx.fillStyle = color || "rgba(255,255,255,0.5)";
        const mid = height / 2;
        // 先算出所有柱子的平均值，找到最大值用于归一化
        const avgs = [];
        let peak = 0;
        for (let i = 0; i < bars; i++) {
          let sum = 0;
          const start = i * step;
          for (let j = 0; j < step && (start + j) < raw.length; j++) sum += Math.abs(raw[start + j]);
          const avg = sum / step;
          avgs.push(avg);
          if (avg > peak) peak = avg;
        }
        // 归一化后绘制，最高柱子占满 90% 高度
        const scale = peak > 0 ? (height * 0.9) / peak : 1;
        for (let i = 0; i < bars; i++) {
          const h = Math.max(2, avgs[i] * scale);
          ctx.fillRect(i * (barW + gap), mid - h / 2, barW, h);
        }
      })
      .catch(() => {})
      .finally(() => audioCtx.close().catch(() => {}));
  }, [url, width, height, color]);
  return <canvas ref={canvasRef} style={{ width, height, position: "absolute", inset: 0, pointerEvents: "none" }} />;
});

const TRACK_DEFS = {
  video:     { label: "视频轨", icon: Film,      color: "accentPrimary" },
  image:     { label: "图片轨", icon: ImageIcon, color: "bluePrimary" },
  narration: { label: "旁白轨", icon: Mic,       color: "purplePrimary" },
  bgm:       { label: "BGM轨",  icon: Music,     color: "accentPrimary" },
};

// 轨道排序：视频在最上，然后图片、旁白、BGM
const TRACK_ORDER = { video: 0, image: 1, narration: 2, bgm: 3 };

const fmt = (s) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}`;
};

const RoughCutPage = () => {
  const { projectId, episodeId } = useParams();
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();
  const { project, loading, saveItem, deleteItem } = useRoughCut(episodeId);

  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const playRef = useRef(null);
  const lastFrameRef = useRef(null);
  const [pxPerSec, setPxPerSec] = useState(DEFAULT_PX);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [addDialog, setAddDialog] = useState(null);
  const [newUrl, setNewUrl] = useState("");
  const [newName, setNewName] = useState("");
  const [newDuration, setNewDuration] = useState(3);
  const [showResourceSelector, setShowResourceSelector] = useState(false);
  const [addMode, setAddMode] = useState(null); // 'resource' | 'upload' | 'url'
  const fileInputRef = useRef(null);
  const [dragState, setDragState] = useState(null);
  const timelineScrollRef = useRef(null);
  const narrationAudioRef = useRef(null);
  const bgmAudioRef = useRef(null);
  const isPlayingRef = useRef(false);
  const videoRef = useRef(null);
  const nextVideoPreloadRef = useRef(null);
  const [bgmVolume, setBgmVolume] = useState(0.6);

  // ─── 动态轨道：按 trackType + trackIndex 分组 ───
  const trackRows = useMemo(() => {
    const rows = [];
    const items = project?.items || [];
    const grouped = {};
    items.forEach((it) => {
      const key = `${it.trackType}_${it.trackIndex || 0}`;
      if (!grouped[key]) grouped[key] = { type: it.trackType, index: it.trackIndex || 0, items: [] };
      grouped[key].items.push(it);
    });
    // 确保至少有一行 video/image/narration/bgm
    ["video", "image", "narration", "bgm"].forEach((t) => {
      const key = `${t}_0`;
      if (!grouped[key]) grouped[key] = { type: t, index: 0, items: [] };
    });
    // 排序：video 轨在最上，image、narration、bgm 依次，同类型按 index
    Object.values(grouped)
      .sort((a, b) => ((TRACK_ORDER[a.type] ?? 99) - (TRACK_ORDER[b.type] ?? 99)) || (a.index - b.index))
      .forEach((g) => {
        g.items.sort((a, b) => a.startTime - b.startTime);
        rows.push(g);
      });
    return rows;
  }, [project?.items]);

  const allItems = useMemo(() => project?.items || [], [project?.items]);

  const totalDuration = useMemo(() => {
    let max = 0;
    allItems.forEach((it) => {
      const end = (it.startTime || 0) + (it.duration || 0);
      if (end > max) max = end;
    });
    return Math.max(max, 10);
  }, [allItems]);

  const timelineWidth = totalDuration * pxPerSec + 300;

  const currentImage = useMemo(() => {
    const imgs = allItems.filter((it) => it.trackType === "image");
    for (let i = imgs.length - 1; i >= 0; i--) {
      const it = imgs[i];
      if (currentTime >= it.startTime && currentTime < it.startTime + it.duration) return it.resourceUrl;
    }
    return null;
  }, [currentTime, allItems]);

  // 视频轨片段（按 startTime 排序），用于无缝播放
  const videoItems = useMemo(
    () => allItems.filter((it) => it.trackType === "video" && it.resourceUrl)
      .sort((a, b) => (a.startTime || 0) - (b.startTime || 0)),
    [allItems]
  );

  // 当前时间落在哪个视频片段
  const currentVideoIdx = useMemo(() => {
    for (let i = 0; i < videoItems.length; i++) {
      const it = videoItems[i];
      const st = it.startTime || 0;
      if (currentTime >= st && currentTime < st + (it.duration || 0)) return i;
    }
    return -1;
  }, [currentTime, videoItems]);

  const currentVideo = currentVideoIdx >= 0 ? videoItems[currentVideoIdx] : null;
  const nextVideo = currentVideoIdx >= 0 && currentVideoIdx + 1 < videoItems.length ? videoItems[currentVideoIdx + 1] : null;

  // 当前时间命中的旁白/BGM 素材（渲染期派生，供音频同步 effect 与渲染共用）
  const currentNarration = allItems.filter((it) => it.trackType === "narration").find((it) => currentTime >= it.startTime && currentTime < it.startTime + it.duration);
  const currentBgm = allItems.filter((it) => it.trackType === "bgm").find((it) => currentTime >= it.startTime && currentTime < it.startTime + it.duration);

  // ─── 播放 ───
  const startPlayback = useCallback(() => {
    setIsPlaying(true);
    isPlayingRef.current = true;
    lastFrameRef.current = performance.now();
    const tick = (now) => {
      if (!isPlayingRef.current) return;
      const dt = (now - lastFrameRef.current) / 1000;
      lastFrameRef.current = now;
      setCurrentTime((prev) => {
        const next = prev + dt;
        if (next >= totalDuration) { setIsPlaying(false); isPlayingRef.current = false; return 0; }
        return next;
      });
      playRef.current = requestAnimationFrame(tick);
    };
    playRef.current = requestAnimationFrame(tick);
  }, [totalDuration]);

  const stopPlayback = useCallback(() => {
    setIsPlaying(false);
    isPlayingRef.current = false;
    if (playRef.current) cancelAnimationFrame(playRef.current);
  }, []);

  const togglePlay = useCallback(() => {
    if (isPlayingRef.current) stopPlayback(); else startPlayback();
  }, [startPlayback, stopPlayback]);

  useEffect(() => () => { if (playRef.current) cancelAnimationFrame(playRef.current); }, []);

  // ─── 视频元素同步：让 <video> 跟随播放头，无缝衔接 ───
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !currentVideo) return;
    const localTime = Math.max(0, currentTime - (currentVideo.startTime || 0));
    // 漂移超过 0.3s 才校正，避免每帧 seek 造成卡顿
    if (Math.abs((v.currentTime || 0) - localTime) > 0.3) {
      try { v.currentTime = localTime; } catch { /* 静默忽略，保持原吞错语义 */ }
    }
    if (isPlaying) { const p = v.play(); if (p?.catch) p.catch(() => {}); }
    else v.pause();
  }, [currentVideo, currentTime, isPlaying]);

  // 预加载下一段视频，实现切换无缝
  useEffect(() => {
    const pv = nextVideoPreloadRef.current;
    if (pv && nextVideo?.resourceUrl) { try { pv.load(); } catch { /* 静默忽略，保持原吞错语义 */ } }
  }, [nextVideo?.resourceUrl]);

  // ─── 音频同步：BGM/旁白跟随播放头 + 音量 ───
  useEffect(() => {
    // BGM：背景乐，音量可调；视频原声为主，BGM 作背景
    const bgm = bgmAudioRef.current;
    if (bgm) {
      bgm.volume = bgmVolume;
      if (isPlaying && currentBgm) {
        const local = Math.max(0, currentTime - (currentBgm.startTime || 0));
        if (Math.abs((bgm.currentTime || 0) - local) > 1.0) { try { bgm.currentTime = local; } catch { /* 静默忽略，保持原吞错语义 */ } }
        bgm.play().catch(() => {});
      } else bgm.pause();
    }
    // 旁白：清晰为主，音量 1.0
    const nar = narrationAudioRef.current;
    if (nar) {
      nar.volume = 1.0;
      if (isPlaying && currentNarration) {
        const local = Math.max(0, currentTime - (currentNarration.startTime || 0));
        if (Math.abs((nar.currentTime || 0) - local) > 1.0) { try { nar.currentTime = local; } catch { /* 静默忽略，保持原吞错语义 */ } }
        nar.play().catch(() => {});
      } else nar.pause();
    }
  }, [isPlaying, currentTime, bgmVolume, currentBgm, currentNarration]);

  // ─── 空格键播放/暂停 + Delete 删除选中 ───
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.code === "Space") { e.preventDefault(); togglePlay(); }
      if ((e.code === "Delete" || e.code === "Backspace") && selectedIds.size > 0) {
        e.preventDefault();
        selectedIds.forEach((id) => deleteItem(id, { silent: true }).catch(() => {}));
        setSelectedIds(new Set());
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [togglePlay, selectedIds, deleteItem]);

  // ─── Ctrl+滚轮缩放 ───
  const handleWheel = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      setPxPerSec((v) => Math.min(MAX_PX, Math.max(MIN_PX, v - e.deltaY * 0.5)));
    }
  }, []);

  // ─── 刻度尺 / 空白区域点击 seek ───
  const handleRulerClick = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const t = Math.max(0, (e.clientX - rect.left) / pxPerSec);
    setCurrentTime(t);
  }, [pxPerSec]);

  // ─── 播放头拖拽 ───
  const onPlayheadDown = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    const scrollEl = timelineScrollRef.current;
    if (!scrollEl) return;
    const onMove = (ev) => {
      const rect = scrollEl.getBoundingClientRect();
      const x = ev.clientX - rect.left + scrollEl.scrollLeft;
      setCurrentTime(Math.max(0, Math.min(totalDuration, x / pxPerSec)));
    };
    const onUp = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [pxPerSec, totalDuration]);

  const zoomIn = () => setPxPerSec((v) => Math.min(v + 20, MAX_PX));
  const zoomOut = () => setPxPerSec((v) => Math.max(v - 20, MIN_PX));

  // ─── 添加素材（URL方式） ───
  const handleAdd = async () => {
    if (!newUrl.trim()) { toast.warning("请输入资源URL"); return; }
    const trackItems = trackRows.find((r) => r.type === addDialog.trackType && r.index === 0)?.items || [];
    const lastEnd = trackItems.reduce((m, it) => Math.max(m, (it.startTime || 0) + (it.duration || 0)), 0);
    let dur = newDuration;
    if (addDialog.trackType !== "image") {
      try { dur = await getAudioDuration(newUrl); } catch { /* 静默忽略，保持原吞错语义 */ }
    }
    try {
      await saveItem({ projectId: project.id, trackType: addDialog.trackType, trackIndex: 0, startTime: lastEnd, duration: dur, resourceUrl: newUrl, resourceName: newName || `素材_${Date.now()}`, sortOrder: 0, metadata: addDialog.trackType !== "image" ? JSON.stringify({ originalDuration: dur }) : null });
      setAddDialog(null); setAddMode(null); setNewUrl(""); setNewName(""); setNewDuration(3);
    } catch { /* 静默忽略，保持原吞错语义 */ }
  };

  // ─── 从资源库选择 ───
  const handleResourceSelect = async (resource) => {
    setShowResourceSelector(false);
    if (!addDialog || !resource?.url) return;
    const trackItems = trackRows.find((r) => r.type === addDialog.trackType && r.index === 0)?.items || [];
    const lastEnd = trackItems.reduce((m, it) => Math.max(m, (it.startTime || 0) + (it.duration || 0)), 0);
    let dur = newDuration || 3;
    if (addDialog.trackType !== "image") {
      try { dur = await getAudioDuration(resource.url); } catch { /* 静默忽略，保持原吞错语义 */ }
    }
    try {
      await saveItem({ projectId: project.id, trackType: addDialog.trackType, trackIndex: 0, startTime: lastEnd, duration: dur, resourceUrl: resource.url, resourceName: resource.name || `素材_${Date.now()}`, sortOrder: 0, metadata: addDialog.trackType !== "image" ? JSON.stringify({ originalDuration: dur }) : null });
      setAddDialog(null); setAddMode(null); setNewUrl(""); setNewName(""); setNewDuration(3);
    } catch { /* 静默忽略，保持原吞错语义 */ }
  };

  // ─── 本地上传 ───
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !addDialog) return;
    if (fileInputRef.current) fileInputRef.current.value = "";
    toast.info("上传中...");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const url = await authenticatedFetch(`${API_HOST}/api/upload/asset`, { method: "POST", body: formData });
      const trackItems = trackRows.find((r) => r.type === addDialog.trackType && r.index === 0)?.items || [];
      const lastEnd = trackItems.reduce((m, it) => Math.max(m, (it.startTime || 0) + (it.duration || 0)), 0);
      let dur = newDuration || 3;
      if (addDialog.trackType !== "image") {
        try { dur = await getAudioDuration(url); } catch { /* 静默忽略，保持原吞错语义 */ }
      }
      await saveItem({ projectId: project.id, trackType: addDialog.trackType, trackIndex: 0, startTime: lastEnd, duration: dur, resourceUrl: url, resourceName: file.name, sortOrder: 0, metadata: addDialog.trackType !== "image" ? JSON.stringify({ originalDuration: dur }) : null });
      setAddDialog(null); setAddMode(null); setNewDuration(3);
    } catch (err) {
      toast.error("上传失败: " + (err.message || ""));
    }
  };

  // ─── 拖拽：move / resize-left / resize-right（支持多选） ───
  const onClipMouseDown = useCallback((e, item, mode) => {
    e.stopPropagation(); e.preventDefault();
    // Shift 多选：只切换选中状态，不启动拖拽
    if (e.shiftKey) {
      setSelectedIds((prev) => { const next = new Set(prev); if (next.has(item.id)) next.delete(item.id); else next.add(item.id); return next; });
      return;
    }
    // 非 Shift：如果没选中当前 item，先单选它
    if (!selectedIds.has(item.id)) {
      setSelectedIds(new Set([item.id]));
    }
    // resize 只对单个生效
    if (mode !== "move") {
      setDragState({ items: [{ item, origStart: item.startTime, origDur: item.duration }], mode, startX: e.clientX });
      return;
    }
    // move：拖动所有选中的（如果当前 item 在选中集合里）
    const ids = selectedIds.has(item.id) ? selectedIds : new Set([item.id]);
    const dragItems = allItems.filter((it) => ids.has(it.id)).map((it) => ({ item: it, origStart: it.startTime, origDur: it.duration }));
    setDragState({ items: dragItems, mode, startX: e.clientX });
  }, [selectedIds, allItems]);

  useEffect(() => {
    if (!dragState) return;
    const onMove = (e) => {
      const dx = e.clientX - dragState.startX;
      const dt = dx / pxPerSec;
      dragState.items.forEach(({ item, origStart, origDur }) => {
        // 解析音频原始时长上限
        const isAudio = item.trackType === "narration" || item.trackType === "bgm";
        let maxDur = Infinity;
        if (isAudio) {
          try { const m = JSON.parse(item.metadata || "{}"); if (m.originalDuration) maxDur = m.originalDuration; } catch { /* 静默忽略，保持原吞错语义 */ }
          if (maxDur === Infinity) maxDur = origDur; // 没有 metadata 就用当前时长作为上限
        }

        if (dragState.mode === "move") {
          item._tempStart = Math.max(0, origStart + dt);
        } else if (dragState.mode === "resize-right") {
          const newDur = Math.max(0.5, origDur + dt);
          item._tempDur = isAudio ? Math.min(newDur, maxDur) : newDur;
        } else if (dragState.mode === "resize-left") {
          const maxShrink = origDur - 0.5;
          const shift = Math.min(Math.max(dt, -origStart), maxShrink);
          const newDur = origDur - shift;
          // 音频左拖：缩短可以，但总时长不能超过原始时长
          if (isAudio && newDur > maxDur) {
            const clampedShift = origDur - maxDur;
            item._tempStart = origStart + clampedShift;
            item._tempDur = maxDur;
          } else {
            item._tempStart = origStart + shift;
            item._tempDur = newDur;
          }
        }
      });
      setDragState((s) => ({ ...s }));
    };
    const onUp = async () => {
      const toSave = [];
      dragState.items.forEach(({ item }) => {
        const updated = { ...item };
        delete updated._tempStart; delete updated._tempDur;
        if (item._tempStart !== undefined) updated.startTime = Math.round(item._tempStart * 10) / 10;
        if (item._tempDur !== undefined) updated.duration = Math.round(item._tempDur * 10) / 10;
        delete item._tempStart; delete item._tempDur;
        toSave.push(updated);
      });
      setDragState(null);
      for (const u of toSave) { try { await saveItem(u, { silent: true }); } catch { /* 静默忽略，保持原吞错语义 */ } }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [dragState, pxPerSec, saveItem]);

  const handleDelete = async (itemId) => { try { await deleteItem(itemId); } catch { /* 静默忽略，保持原吞错语义 */ } };

  if (loading) {
    return (<div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: "var(--color-bg-primary)" }}><div style={{ color: "var(--color-text-muted)" }}>加载中...</div></div>);
  }

  // ─── 渲染 ───
  const renderRuler = () => {
    const ticks = [];
    let interval = 1;
    if (pxPerSec < 40) interval = 5; else if (pxPerSec < 80) interval = 2;
    for (let t = 0; t <= totalDuration + 5; t += interval) {
      ticks.push(
        <div key={t} className="absolute top-0 flex flex-col items-center" style={{ left: t * pxPerSec, height: RULER_H }}>
          <div className="w-px h-2" style={{ backgroundColor: "var(--color-text-muted)" }} />
          <span className="select-none" style={{ color: "var(--color-text-muted)", fontSize: 9 }}>{fmt(t)}</span>
        </div>
      );
    }
    return ticks;
  };

  const renderClip = (item, trackType) => {
    const def = TRACK_DEFS[trackType];
    const st = item._tempStart !== undefined ? item._tempStart : item.startTime;
    const dur = item._tempDur !== undefined ? item._tempDur : item.duration;
    const left = st * pxPerSec;
    const width = Math.max(dur * pxPerSec, 16);
    const isImg = trackType === "image";
    const color = theme.colors[def.color];
    const isSel = selectedIds.has(item.id);

    return (
      <div
        key={item.id}
        className="absolute top-1 group rounded"
        style={{
          left, width, height: TRACK_H - 8,
          backgroundColor: `${color}${isSel ? "50" : "25"}`,
          border: `${isSel ? 2 : 1}px solid ${isSel ? color : color + "70"}`,
          cursor: dragState ? "grabbing" : "grab",
          overflow: "hidden", userSelect: "none", zIndex: isSel ? 10 : 1,
        }}
        onMouseDown={(e) => onClipMouseDown(e, item, "move")}
        onClick={(e) => e.stopPropagation()}
      >
        {isImg && item.resourceUrl && (
          <img src={item.resourceUrl} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50 pointer-events-none" draggable={false} />
        )}
        {!isImg && item.resourceUrl && width > 20 && (
          <AudioWaveform url={item.resourceUrl} width={Math.floor(width)} height={TRACK_H - 8} color={`${color}90`} />
        )}
        {/* 文字：音频放顶部小标签，图片居中 */}
        {isImg ? (
          <div className="relative z-10 px-1.5 flex items-center gap-1 h-full pointer-events-none" style={{ minWidth: 0 }}>
            <span className="font-medium truncate" style={{ color: "#fff", fontSize: 10, textShadow: "0 1px 2px rgba(0,0,0,0.8)" }}>{item.resourceName}</span>
            {width > 50 && <span className="opacity-70 shrink-0" style={{ color: "#fff", fontSize: 9, textShadow: "0 1px 2px rgba(0,0,0,0.8)" }}>{dur.toFixed(1)}s</span>}
          </div>
        ) : (
          <div className="absolute top-0 left-1 right-1 z-10 flex items-center gap-1 pointer-events-none" style={{ height: 14 }}>
            <span className="truncate" style={{ color: "#fff", fontSize: 9, textShadow: "0 1px 2px rgba(0,0,0,0.8)", opacity: 0.8 }}>{item.resourceName}</span>
            {width > 50 && <span className="shrink-0" style={{ color: "#fff", fontSize: 8, textShadow: "0 1px 2px rgba(0,0,0,0.8)", opacity: 0.6 }}>{dur.toFixed(1)}s</span>}
          </div>
        )}
        {/* 左手柄 */}
        <div className="absolute top-0 left-0 w-3 h-full cursor-col-resize hover:bg-white/20 transition-all duration-200 z-20" style={{ backgroundColor: isSel ? `${color}90` : "transparent" }} onMouseDown={(e) => onClipMouseDown(e, item, "resize-left")} />
        {/* 右手柄 */}
        <div className="absolute top-0 right-0 w-3 h-full cursor-col-resize hover:bg-white/20 transition-all duration-200 z-20" style={{ backgroundColor: isSel ? `${color}90` : "transparent" }} onMouseDown={(e) => onClipMouseDown(e, item, "resize-right")} />
        {/* 删除 */}
        <button className="absolute top-0.5 right-2 p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity z-20" style={{ backgroundColor: "var(--overlay-medium)" }} onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} onMouseDown={(e) => e.stopPropagation()}>
          <Trash2 size={10} style={{ color: "#fff" }} />
        </button>
      </div>
    );
  };

  const playheadLeft = currentTime * pxPerSec;
  const selectedItem = selectedIds.size === 1 ? allItems.find((it) => selectedIds.has(it.id)) : null;
  const totalTracksH = trackRows.length * TRACK_H;

  return (
    <div className="w-full h-full flex flex-col font-sans select-none overflow-hidden" style={{ backgroundColor: "var(--color-bg-primary)" }} onClick={() => setSelectedIds(new Set())} tabIndex={-1}>

      {/* ═══ Header ═══ */}
      <div className="h-12 border-b flex items-center justify-between px-4 shrink-0" style={{ borderColor: "var(--color-border-primary)" }}>
        <div className="flex items-center gap-3">
          <Link to={`/project/${projectId}/episode/${episodeId}/storyboard`} className="p-1.5 rounded-lg" style={{ color: "var(--color-text-muted)", backgroundColor: "var(--color-bg-tertiary)" }}><ArrowLeft size={16} /></Link>
          <h1 className="font-bold text-sm" style={{ color: "var(--color-text-primary)" }}>粗剪台</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={togglePlay} className="p-2 rounded-lg" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
            {isPlaying ? <Pause size={16} style={{ color: HEAD_COLOR }} /> : <Play size={16} style={{ color: "var(--color-text-primary)" }} />}
          </button>
          <span className="text-xs font-mono" style={{ color: "var(--color-text-muted)" }}>{fmt(currentTime)} / {fmt(totalDuration)}</span>
          <div className="flex items-center gap-1">
            <button onClick={zoomOut} className="p-1 rounded" style={{ color: "var(--color-text-muted)" }}><ZoomOut size={14} /></button>
            <span style={{ color: "var(--color-text-muted)", fontSize: 10, width: 32, textAlign: "center", display: "inline-block" }}>{pxPerSec}px</span>
            <button onClick={zoomIn} className="p-1 rounded" style={{ color: "var(--color-text-muted)" }}><ZoomIn size={14} /></button>
          </div>
        </div>
      </div>

      {/* ═══ Preview ═══（功能色：视频播放器画面区固定纯黑，任何主题下一致）*/}
      <div className="flex-1 min-h-0 flex items-center justify-center relative" style={{ backgroundColor: "#000" }}>
        {currentVideo ? (
          <video
            ref={videoRef}
            src={currentVideo.resourceUrl}
            className="max-w-full max-h-full object-contain"
            preload="auto"
            playsInline
            onEnded={() => {
              // 当前片段播完，跳到下一段起点实现无缝衔接
              if (nextVideo) setCurrentTime((nextVideo.startTime || 0) + 0.01);
              else { setIsPlaying(false); isPlayingRef.current = false; }
            }}
          />
        ) : currentImage ? (
          <img src={currentImage} alt="preview" className="max-w-full max-h-full object-contain" draggable={false} />
        ) : (
          <div style={{ color: "var(--color-text-muted)", fontSize: 14 }}>无画面</div>
        )}
        {/* 预加载下一段视频，切换无缝 */}
        {nextVideo && <video ref={nextVideoPreloadRef} src={nextVideo.resourceUrl} preload="auto" muted style={{ display: "none" }} />}
        {selectedItem && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full animate-in fade-in slide-in-from-bottom-2 duration-200" style={{ backgroundColor: "var(--overlay-heavy)", color: "#fff", fontSize: 11 }}>
            {selectedItem.resourceName} · {selectedItem.duration?.toFixed(1)}s · {fmt(selectedItem.startTime || 0)}
          </div>
        )}
      </div>

      {/* ═══ 整体进度条（外部，可拖拽 seek） ═══ */}
      <div className="shrink-0 px-4 py-2 flex items-center gap-3 border-t" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
        <span className="text-xs font-mono shrink-0" style={{ color: "var(--color-text-muted)", width: 44, textAlign: "right" }}>{fmt(currentTime)}</span>
        <div
          className="relative flex-1 h-2 rounded-full cursor-pointer"
          style={{ backgroundColor: "var(--color-bg-tertiary)" }}
          onMouseDown={(e) => {
            const seek = (clientX, rect) => setCurrentTime(Math.max(0, Math.min(totalDuration, ((clientX - rect.left) / rect.width) * totalDuration)));
            const rect = e.currentTarget.getBoundingClientRect();
            seek(e.clientX, rect);
            const onMove = (ev) => seek(ev.clientX, rect);
            const onUp = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
            window.addEventListener("mousemove", onMove);
            window.addEventListener("mouseup", onUp);
          }}
        >
          <div className="absolute top-0 left-0 h-full rounded-full" style={{ width: `${totalDuration > 0 ? (currentTime / totalDuration) * 100 : 0}%`, backgroundColor: HEAD_COLOR }} />
          <div className="absolute top-1/2 -translate-y-1/2 rounded-full shadow" style={{ left: `${totalDuration > 0 ? (currentTime / totalDuration) * 100 : 0}%`, width: 10, height: 10, marginLeft: -5, backgroundColor: HEAD_COLOR }} />
        </div>
        <span className="text-xs font-mono shrink-0" style={{ color: "var(--color-text-muted)", width: 44 }}>{fmt(totalDuration)}</span>
        {/* BGM 音量 */}
        <div className="flex items-center gap-1.5 shrink-0 ml-2" title="BGM 背景音量">
          <Volume2 size={14} style={{ color: theme.colors.accentPrimary }} />
          <input type="range" min={0} max={1} step={0.05} value={bgmVolume} onChange={(e) => setBgmVolume(parseFloat(e.target.value))} style={{ width: 80 }} />
        </div>
      </div>

      {/* ═══ Timeline ═══ */}
      <div className="shrink-0 border-t" style={{ borderColor: "var(--color-border-primary)", height: RULER_H + totalTracksH + 2 }} onWheel={handleWheel}>
        <div className="flex h-full">
          {/* 左侧标签 */}
          <div className="shrink-0 flex flex-col" style={{ width: TRACK_LABEL_W, borderRight: "1px solid var(--color-border-primary)" }}>
            <div className="shrink-0 flex items-center justify-center" style={{ height: RULER_H, borderBottom: "1px solid var(--color-border-primary)" }}>
              <span style={{ color: "var(--color-text-muted)", fontSize: 9 }}>时间轴</span>
            </div>
            {trackRows.map((row) => {
              const def = TRACK_DEFS[row.type];
              const Icon = def.icon;
              return (
                <div key={`${row.type}_${row.index}`} className="shrink-0 flex items-center justify-between px-2" style={{ height: TRACK_H, borderBottom: "1px solid var(--color-border-primary)" }}>
                  <div className="flex items-center gap-1.5">
                    <Icon size={12} style={{ color: theme.colors[def.color] }} />
                    <span style={{ color: "var(--color-text-primary)", fontSize: 11 }}>{def.label}{row.index > 0 ? ` ${row.index + 1}` : ""}</span>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); setAddDialog({ trackType: row.type }); }} className="p-0.5 rounded" style={{ color: theme.colors[def.color] }}><Plus size={12} /></button>
                </div>
              );
            })}
          </div>

          {/* 右侧时间轴 */}
          <div className="flex-1 overflow-x-auto overflow-y-hidden no-scrollbar" ref={timelineScrollRef}>
            <div className="relative" style={{ width: timelineWidth, height: "100%" }}>
              {/* 刻度尺 */}
              <div className="sticky top-0 relative cursor-pointer" style={{ height: RULER_H, borderBottom: "1px solid var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }} onClick={handleRulerClick}>
                {renderRuler()}
              </div>
              {/* 轨道 */}
              {trackRows.map((row) => (
                <div key={`${row.type}_${row.index}`} className="relative" style={{ height: TRACK_H, borderBottom: "1px solid var(--color-border-primary)" }} onClick={handleRulerClick}>
                  {row.items.map((item) => renderClip(item, row.type))}
                </div>
              ))}
              {/* 播放头 */}
              <div className="absolute top-0 pointer-events-none" style={{ left: playheadLeft, width: 2, height: "100%", backgroundColor: HEAD_COLOR, zIndex: 50 }}>
                <div onMouseDown={onPlayheadDown} className="pointer-events-auto cursor-col-resize" style={{ position: "absolute", top: 0, left: -5, width: 12, height: 14, backgroundColor: HEAD_COLOR, clipPath: "polygon(0 0, 100% 0, 50% 100%)", borderRadius: "2px 2px 0 0" }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 隐藏音频 */}
      {currentNarration && <audio ref={narrationAudioRef} src={currentNarration.resourceUrl} preload="auto" />}
      {currentBgm && <audio ref={bgmAudioRef} src={currentBgm.resourceUrl} preload="auto" loop />}

      {/* ═══ 添加弹窗 ═══ */}
      {addDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center animate-in fade-in duration-200" style={{ backgroundColor: "var(--overlay-medium)" }} onClick={() => { setAddDialog(null); setAddMode(null); }}>
          <div className="rounded-xl p-5 w-96 space-y-3 animate-in fade-in zoom-in-95 duration-200" style={{ backgroundColor: "var(--color-bg-secondary)", border: "1px solid var(--color-border-primary)" }} onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-sm" style={{ color: "var(--color-text-primary)" }}>添加到 {TRACK_DEFS[addDialog.trackType]?.label}</h3>

            {/* 三个入口按钮 */}
            {!addMode && (
              <div className="space-y-2">
                <button onClick={() => { fileInputRef.current?.click(); }} className="w-full px-3 py-2.5 rounded-lg text-sm flex items-center gap-3 transition-all duration-200" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-primary)", border: "1px solid var(--color-border-primary)" }}>
                  <Upload size={16} style={{ color: theme.colors.greenPrimary }} /> 本地上传
                  <span className="ml-auto text-xs" style={{ color: "var(--color-text-muted)" }}>{addDialog.trackType === "image" ? "图片文件" : "音频文件"}</span>
                </button>
                <button onClick={() => setShowResourceSelector(true)} className="w-full px-3 py-2.5 rounded-lg text-sm flex items-center gap-3 transition-all duration-200" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-primary)", border: "1px solid var(--color-border-primary)" }}>
                  <FolderOpen size={16} style={{ color: theme.colors[TRACK_DEFS[addDialog.trackType]?.color || "bluePrimary"] }} /> 资源管理器
                  <span className="ml-auto text-xs" style={{ color: "var(--color-text-muted)" }}>项目素材库</span>
                </button>
                <button onClick={() => setAddMode("url")} className="w-full px-3 py-2.5 rounded-lg text-sm flex items-center gap-3 transition-all duration-200" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-primary)", border: "1px solid var(--color-border-primary)" }}>
                  <Globe size={16} style={{ color: theme.colors.accentPrimary }} /> 网络地址
                  <span className="ml-auto text-xs" style={{ color: "var(--color-text-muted)" }}>输入URL</span>
                </button>
              </div>
            )}

            {/* URL 输入表单 */}
            {addMode === "url" && (
              <div className="space-y-3">
                <input className="w-full px-3 py-2 rounded text-sm outline-none" style={{ backgroundColor: "var(--color-bg-overlay)", color: "var(--color-text-primary)", border: "1px solid var(--color-border-primary)" }} placeholder={addDialog.trackType === "image" ? "图片 URL" : "音频 URL"} value={newUrl} onChange={(e) => setNewUrl(e.target.value)} autoFocus />
                <input className="w-full px-3 py-2 rounded text-sm outline-none" style={{ backgroundColor: "var(--color-bg-overlay)", color: "var(--color-text-primary)", border: "1px solid var(--color-border-primary)" }} placeholder="名称（可选）" value={newName} onChange={(e) => setNewName(e.target.value)} />
                <div className="flex items-center gap-2">
                  <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>时长:</span>
                  <input type="number" step="0.5" min="0.5" className="px-2 py-1 rounded text-sm outline-none w-20" style={{ backgroundColor: "var(--color-bg-overlay)", color: "var(--color-text-primary)", border: "1px solid var(--color-border-primary)" }} value={newDuration} onChange={(e) => setNewDuration(parseFloat(e.target.value) || 3)} />
                  <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>秒</span>
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setAddMode(null)} className="px-4 py-1.5 rounded text-xs" style={{ color: "var(--color-text-muted)" }}>返回</button>
                  <button onClick={handleAdd} className="px-4 py-1.5 rounded text-xs font-bold" style={{ backgroundColor: theme.colors[TRACK_DEFS[addDialog.trackType]?.color || "bluePrimary"], color: "white" }}>确定</button>
                </div>
              </div>
            )}

            {/* 底部取消 */}
            {!addMode && (
              <div className="flex justify-end pt-1">
                <button onClick={() => setAddDialog(null)} className="px-4 py-1.5 rounded text-xs" style={{ color: "var(--color-text-muted)" }}>取消</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 隐藏的文件上传 input */}
      <input ref={fileInputRef} type="file" className="hidden" accept={addDialog?.trackType === "image" ? "image/*" : "audio/*"} onChange={handleFileUpload} />

      {/* 资源选择器 */}
      <ResourceSelector isOpen={showResourceSelector} onClose={() => setShowResourceSelector(false)} onSelect={handleResourceSelect} projectId={projectId} type={addDialog?.trackType === "image" ? "image" : "all"} title={`选择${TRACK_DEFS[addDialog?.trackType]?.label || ""}素材`} />
    </div>
  );
};

export default RoughCutPage;
